// Interface
import { LogicModule } from "../modules/LogicModule";
import { EntityOperationType } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
// Strings
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Utilities
import * as StringHelper from "@fluentui/utilities";
import { MaxTitleCharactersForToast } from "@ms/uno-constants/lib/local/UIConstants";
import { DefaultBasicPlanViewType, UndoSupportedRoutes } from "@ms/uno-constants/lib/local/RoutingConstants";
import { extractEntityRoutingIdFromSubRoute, extractPlanRoutingDetailsFromSubRoute } from "@ms/uno-routing";
import { generateAjaxClientError } from "@ms/uno-services/lib/local/utilities/ServiceUtilities";
import { truncateString } from "@ms/uno-utilities/lib/local/StringUtilities";
/**
 * Resolve the operation scope id that keys the undo/redo stacks for the current route.
 *
 * Entries are pushed and read back under this key, so every call site must derive it identically -
 * deriving it differently on either side silently strands the entries in a scope nothing reads.
 * @param stores Store loader
 * @returns The operation scope id, or an empty string when the current route does not support undo
 */ export function resolveUndoOperationScopeId(stores) {
    const routeStore = stores.load(Store.Route);
    const subRouteType = routeStore.getCurrentSubRouteType();
    if (!UndoSupportedRoutes.has(subRouteType)) {
        return "";
    }
    return extractEntityRoutingIdFromSubRoute(routeStore.getCurrentSubRoute(), subRouteType) ?? "";
}
/**
 * Resolve the current view context (e.g. Grid, Board, Timeline) for undo/redo telemetry.
 *
 * Undo is only wired for basic plans today, so we read the active view from the basic-plan route,
 * falling back to the basic-plan default (Board) when the route omits the view segment.
 * @param stores Store loader
 * @returns The active {@link ViewType} value, or null when the current route does not support undo -
 * a null result signals we're resolving view context off an undo route, which callers use to skip telemetry
 */ export function resolveUndoViewContext(stores) {
    const routeStore = stores.load(Store.Route);
    const subRouteType = routeStore.getCurrentSubRouteType();
    if (!UndoSupportedRoutes.has(subRouteType)) {
        return null;
    }
    const viewType = extractPlanRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), subRouteType)?.viewType;
    return viewType ?? DefaultBasicPlanViewType;
}
/**
 * Builds an `unDeleteEntityAsync` implementation for entity types that have no undelete API.
 * Undelete is for Task, Goals and Buckets only today; every other handler rejects with a structured error so the
 * caller's catch can log consistent telemetry. Will add proper undelete support for other entity types when apis are ready.
 */ function unsupportedUndelete(entityType) {
    return async ()=>{
        throw generateAjaxClientError(null, `unDeleteEntityAsync is not supported for entity type ${entityType}`);
    };
}
/**
 * Builds the handler table — one entry per supported entity type.
 * @param stores Store loader used to read baseline deltas and current entities.
 * @param moduleLoader Logic module loader used to persist changes on the server.
 */ export function getEntityHandlers(stores, moduleLoader) {
    return {
        [EntityType.Task]: {
            getEntity: (id, readBaseline)=>stores.load(Store.Tasks).getTask(id, readBaseline).data,
            getBaselineChainPair: (id, deltaKey)=>stores.load(Store.Tasks).getBaselineChainPair(id, deltaKey),
            updateEntityAsync: async (currentEntity, updatedEntity)=>{
                const module = await moduleLoader.load(LogicModule.Task);
                // This handler only runs during an undo/redo reversal, so flag the update as such. The Task
                // module uses this flag to normalize re-added checklist orderHints and to prevent the update
                // from being re-recorded onto the undo stack.
                await module.updateTaskAsync(currentEntity, updatedEntity, {
                    isUndoRedoOperation: true
                });
            },
            deleteEntityAsync: async (id)=>{
                const module = await moduleLoader.load(LogicModule.Task);
                // This handler only runs during an undo/redo reversal (undoing a create), so flag the
                // delete as such to prevent it from being re-recorded onto the undo stack. The returned
                // Operation-Id is what undeleting this entity again needs.
                return await module.deleteTaskAsync(id, {
                    isUndoRedoOperation: true
                });
            },
            unDeleteEntityAsync: async (deletedEntity, operationId)=>{
                const module = await moduleLoader.load(LogicModule.Task);
                await module.undeleteTaskAsync(deletedEntity, operationId);
            }
        },
        [EntityType.Bucket]: {
            getEntity: (id, readBaseline)=>stores.load(Store.Bucket).getBucket(id, readBaseline).data,
            getBaselineChainPair: (id, deltaKey)=>stores.load(Store.Bucket).getBaselineChainPair(id, deltaKey),
            updateEntityAsync: async (currentEntity, updatedEntity)=>{
                const module = await moduleLoader.load(LogicModule.Bucket);
                await module.updateBucketAsync(currentEntity, updatedEntity, {
                    isUndoRedoOperation: true
                });
            },
            deleteEntityAsync: async (id)=>{
                const module = await moduleLoader.load(LogicModule.Bucket);
                return await module.deleteBucketAsync(id, {
                    isUndoRedoOperation: true
                });
            },
            unDeleteEntityAsync: async (deletedEntity, operationId)=>{
                const module = await moduleLoader.load(LogicModule.Bucket);
                await module.undeleteBucketAsync(deletedEntity, operationId);
            }
        },
        [EntityType.Plan]: {
            getEntity: (id, readBaseline)=>stores.load(Store.Plan).getPlan(id, readBaseline).data,
            getBaselineChainPair: (id, deltaKey)=>stores.load(Store.Plan).getBaselineChainPair(id, deltaKey),
            updateEntityAsync: async (currentEntity, updatedEntity)=>{
                const module = await moduleLoader.load(LogicModule.Plan);
                await module.updatePlanAsync(currentEntity, updatedEntity, {
                    isUndoRedoOperation: true
                });
            },
            deleteEntityAsync: async (id)=>{
                const module = await moduleLoader.load(LogicModule.Plan);
                await module.deletePlanAsync(id);
                return null;
            },
            unDeleteEntityAsync: unsupportedUndelete(EntityType.Plan)
        },
        [EntityType.Goal]: {
            getEntity: (id, readBaseline)=>stores.load(Store.Goal).getGoal(id, readBaseline).data,
            getBaselineChainPair: (id, deltaKey)=>stores.load(Store.Goal).getBaselineChainPair(id, deltaKey),
            updateEntityAsync: async (currentEntity, updatedEntity)=>{
                const module = await moduleLoader.load(LogicModule.Goal);
                await module.updateGoalAsync(currentEntity, updatedEntity, {
                    isUndoRedoOperation: true
                });
            },
            deleteEntityAsync: async (id)=>{
                const module = await moduleLoader.load(LogicModule.Goal);
                return await module.deleteGoalAsync(id, {
                    isUndoRedoOperation: true
                });
            },
            unDeleteEntityAsync: async (deletedEntity, operationId)=>{
                const module = await moduleLoader.load(LogicModule.Goal);
                await module.undeleteGoalAsync(deletedEntity, operationId);
            }
        },
        [EntityType.Dependency]: {
            getEntity: (id, readBaseline)=>stores.load(Store.Dependency).getDependency(id, readBaseline).data,
            getBaselineChainPair: (id, deltaKey)=>stores.load(Store.Dependency).getBaselineChainPair(id, deltaKey),
            updateEntityAsync: async (currentEntity, updatedEntity)=>{
                const module = await moduleLoader.load(LogicModule.Dependency);
                await module.updateDependencyAsync(currentEntity, updatedEntity, {
                    isUndoRedoOperation: true
                });
            },
            deleteEntityAsync: async (id)=>{
                const module = await moduleLoader.load(LogicModule.Dependency);
                await module.deleteDependencyAsync(id, {
                    isUndoRedoOperation: true
                });
                return null;
            },
            unDeleteEntityAsync: unsupportedUndelete(EntityType.Dependency)
        }
    };
}
/**
 * Resolves display metadata (name + type) for an undo/redo toast from the entry's snapshot.
 * @param wasUndoTriggeredForOptimisticOp When true, reads the committed baseline value readFromBaseline as true; when false, reads the current value
 * (baseline + resumed delta). The caller picks this based on whether the undo reconciled an optimistic op,
 * since only the current value reflects the name the user saw in that case.
 */ export function resolveUndoRedoToastEntityContext(entry, entityHandlers, wasUndoTriggeredForOptimisticOp) {
    const snapshot = entry.snapshots[0];
    if (!snapshot) {
        return {};
    }
    const entity = entityHandlers[snapshot.entityType]?.getEntity(snapshot.entityId, !wasUndoTriggeredForOptimisticOp) ?? null;
    return {
        entityDisplayName: getEntityDisplayName(entity, snapshot.entityType),
        entityType: snapshot.entityType
    };
}
/**
 * Returns the display name for an entity, or the entity type if the entity is null.
 * Entity names are truncated to the toast title limit; the entity-type fallbacks are already short.
 */ function getEntityDisplayName(entity, entityType) {
    if (entity == null) {
        return entityType;
    }
    switch(entityType){
        case EntityType.Task:
            return truncateEntityName(entity.displayName);
        case EntityType.Bucket:
            return truncateEntityName(entity.displayName);
        case EntityType.Plan:
            return truncateEntityName(entity.displayName);
        case EntityType.Goal:
            return truncateEntityName(entity.displayName);
        case EntityType.Dependency:
        default:
            return getLocalizedEntityTypeLabel(entityType);
    }
}
/** Truncates an entity display name to the toast title limit, preserving nullish values as-is. */ function truncateEntityName(displayName) {
    return displayName ? truncateString(displayName, MaxTitleCharactersForToast) : displayName;
}
/**
 * Builds undo/redo toasts for operation outcomes.
 */ export function buildUndoRedoToast(context) {
    const { operationType, entityDisplayName, entityType, undoRedoSucceeded, isUndo, reverseAction } = context;
    if (!entityDisplayName) {
        return null;
    }
    if (undoRedoSucceeded) {
        const successTitle = getSuccessTitle(operationType, entityDisplayName, isUndo);
        if (!successTitle) {
            return null;
        }
        return {
            title: successTitle,
            intent: "success",
            isHidden: false,
            ...reverseAction && isUndo && operationType === EntityOperationType.Create ? {
                action: {
                    label: Strings.Toasts_UndoButtonText,
                    onClick: reverseAction,
                    dismissOnAction: true
                }
            } : {}
        };
    }
    const failureTitle = getFailureTitle(operationType, entityDisplayName, isUndo, entityType);
    if (!failureTitle) {
        return null;
    }
    return {
        title: failureTitle,
        intent: "error",
        isHidden: false
    };
}
/**
 * Builds the warning toast shown when only part of a batch undo operation succeeds.
 */ export function buildPartialBatchUndoFailureToast(restoredCount, totalCount, entityType) {
    const entityTypeLabel = getLocalizedEntityTypePluralLabel(entityType);
    return {
        title: StringHelper.format(Strings.Toasts_UndoPartialRestoreTitle, restoredCount, totalCount, entityTypeLabel),
        intent: "warning",
        isHidden: false,
        body: {
            content: StringHelper.format(Strings.Toasts_UndoPartialRestoreSubtitle, entityTypeLabel)
        }
    };
}
/**
 * Returns the title string for an undo/redo success toast.
 * The wording follows the resulting state, not the direction: undoing a delete and redoing a create both
 * restore the entity; undoing a create and redoing a delete both remove it. Only updates differ — undo
 * reverts the change while redo re-applies it.
 */ function getSuccessTitle(operationType, entityDisplayName, isUndo) {
    if (isUndo) {
        switch(operationType){
            case EntityOperationType.Delete:
                return StringHelper.format(Strings.Toasts_RestoredEntityToastContent, entityDisplayName);
            case EntityOperationType.Create:
                return StringHelper.format(Strings.Toasts_DeletedEntityContent, entityDisplayName);
            case EntityOperationType.Update:
                return StringHelper.format(Strings.Toasts_RestoredEntityUpdatesContent, entityDisplayName);
            default:
                return null;
        }
    } else {
        switch(operationType){
            case EntityOperationType.Delete:
                return StringHelper.format(Strings.Toasts_DeletedEntityContent, entityDisplayName);
            case EntityOperationType.Create:
                return StringHelper.format(Strings.Toasts_RestoredEntityToastContent, entityDisplayName);
            case EntityOperationType.Update:
                return StringHelper.format(Strings.Toasts_ReappliedEntityUpdatesContent, entityDisplayName);
            default:
                return null;
        }
    }
}
/**
 * Returns the title string for an undo/redo failure toast. Mirrors {@link getSuccessTitle}: the message
 * describes the action that failed to take effect, so it depends on both the operation type and direction.
 */ function getFailureTitle(operationType, entityDisplayName, isUndo, entityType) {
    const entityTypeLabel = entityType ? getLocalizedEntityTypeLabel(entityType) : "";
    if (isUndo) {
        switch(operationType){
            case EntityOperationType.Delete:
                return StringHelper.format(Strings.Notifications_RestoreEntityError, entityDisplayName);
            case EntityOperationType.Create:
                return StringHelper.format(Strings.Notifications_DeleteEntityError, entityDisplayName);
            case EntityOperationType.Update:
                return StringHelper.format(Strings.Notifications_RestoreChangesError, entityTypeLabel, entityDisplayName);
            default:
                return null;
        }
    } else {
        switch(operationType){
            case EntityOperationType.Delete:
                return StringHelper.format(Strings.Notifications_DeleteEntityError, entityDisplayName);
            case EntityOperationType.Create:
                return StringHelper.format(Strings.Notifications_RestoreEntityError, entityDisplayName);
            case EntityOperationType.Update:
                return StringHelper.format(Strings.Notifications_ReapplyChangesError, entityTypeLabel, entityDisplayName);
            default:
                return null;
        }
    }
}
/**
 * Maps an entity type to its localized  noun for display in user-facing strings.
 */ function getLocalizedEntityTypeLabel(entityType) {
    switch(entityType){
        case EntityType.Task:
            return StringHelper.format(Strings.EntityTypeLabel_Task);
        case EntityType.Bucket:
            return StringHelper.format(Strings.EntityTypeLabel_Bucket);
        case EntityType.Plan:
            return StringHelper.format(Strings.EntityTypeLabel_Plan);
        case EntityType.Goal:
            return StringHelper.format(Strings.EntityTypeLabel_Goal);
        case EntityType.Dependency:
            return StringHelper.format(Strings.EntityTypeLabel_Dependency);
        default:
            return StringHelper.format(Strings.EntityTypeLabel_Unknown);
    }
}
/**
 * Maps an entity type to its localized plural noun for display in count-based user-facing strings.
 */ function getLocalizedEntityTypePluralLabel(entityType) {
    switch(entityType){
        case EntityType.Task:
            return StringHelper.format(Strings.EntityTypeLabelPlural_Task);
        case EntityType.Bucket:
            return StringHelper.format(Strings.EntityTypeLabelPlural_Bucket);
        case EntityType.Plan:
            return StringHelper.format(Strings.EntityTypeLabelPlural_Plan);
        case EntityType.Goal:
            return StringHelper.format(Strings.EntityTypeLabelPlural_Goal);
        case EntityType.Dependency:
            return StringHelper.format(Strings.EntityTypeLabelPlural_Dependency);
        default:
            return StringHelper.format(Strings.EntityTypeLabel_Unknown);
    }
}
